import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, X, ArrowUp, Lock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { getApiErrorMessage } from "@/lib/apiErrors";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { usePlanUsage, hasFeature } from "@/hooks/usePlanUsage";
import { isFinanceRole } from "@/lib/roles";

const WELCOME = {
  role: "assistant",
  content: `¡Hola! Soy tu asistente de negocio 👋

Puedo ayudarte con:
- **¿Cuánto he facturado este trimestre?**
- **Prepara el resumen para el Modelo 303**
- **¿Qué facturas tengo pendientes de cobro?**
- **Busca el cliente García**
- **Llévame a presupuestos pendientes de respuesta**

¿En qué te ayudo?`,
};

const STARTERS = [
  "¿Cuánto he facturado este mes?",
  "Resumen fiscal T2 2026",
  "¿Qué facturas están vencidas?",
  "Datos para mi gestor — año 2026",
];

function MdText({ text }) {
  return (
    <div className="space-y-1 leading-relaxed">
      {text.split("\n").map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        const isBullet = line.startsWith("- ");
        const parts = (isBullet ? line.slice(2) : line).split(/\*\*(.*?)\*\*/g);
        const content = parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        );
        return isBullet
          ? <div key={i} className="flex gap-1.5"><span className="text-blueprint mt-1 shrink-0">·</span><span>{content}</span></div>
          : <p key={i}>{content}</p>;
      })}
    </div>
  );
}

export default function AgentWidget() {
  const { user } = useAuth();
  const { role } = useWorkspace();
  const navigate = useNavigate();
  const planUsage = usePlanUsage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const canUse = hasFeature(planUsage, "ai_agent");

  useEffect(() => {
    if (open && canUse) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open, canUse]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");

    const history = [...messages, { role: "user", content: msg }];
    setMessages(history);
    setLoading(true);

    try {
      const apiMessages = history.filter((m) => m.role !== "assistant" || m !== WELCOME);
      const res = await api.post("/ai/agent", { messages: apiMessages });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.message }]);
      for (const action of res.data.actions || []) {
        if (action.type === "navigate") {
          navigate(action.path);
          setOpen(false);
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      const errMsg =
        status === 402 ? "Esta función requiere el plan Estándar o superior." :
        status === 429 ? (e?.response?.data?.detail?.message || "Límite diario alcanzado. Vuelve mañana.") :
        getApiErrorMessage(e, "Error al procesar tu consulta. Inténtalo de nuevo.");
      setMessages((prev) => [...prev, { role: "assistant", content: errMsg }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, navigate]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!user || !isFinanceRole(role)) return null;

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Asistente IA"
        className={`fixed bottom-20 right-4 z-[150] w-12 h-12 flex items-center justify-center border-2 shadow-[4px_4px_0px_#09090B] transition-all active:translate-y-0.5 active:shadow-none ${
          open
            ? "bg-ink text-white border-ink"
            : "bg-blueprint text-white border-blueprint hover:bg-blueprint/90"
        }`}
      >
        {open ? <X size={20} weight="bold" /> : <Sparkle size={20} weight="bold" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-36 right-4 z-[149] w-[min(380px,calc(100vw-2rem))] flex flex-col bg-white border-2 border-ink shadow-[8px_8px_0px_#09090B]"
          style={{ maxHeight: "min(520px, calc(100vh - 10rem))" }}>

          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-ink text-white shrink-0">
            <Sparkle size={16} weight="bold" className="text-blueprint shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm leading-tight">Asistente Factlify</div>
              <div className="text-[10px] text-white/60 font-mono">IA · datos reales de tu negocio</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
              <X size={16} weight="bold" />
            </button>
          </div>

          {!canUse ? (
            /* Upgrade wall */
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-4">
              <div className="w-12 h-12 border-2 border-slate-200 flex items-center justify-center">
                <Lock size={24} weight="bold" className="text-slate-300" />
              </div>
              <div>
                <div className="font-display font-bold text-base mb-1">Función Premium</div>
                <p className="text-sm text-steel">El asistente IA está disponible en el plan <strong>Estándar</strong> o superior.</p>
              </div>
              <button
                type="button"
                onClick={() => { navigate("/billing"); setOpen(false); }}
                className="px-4 py-2.5 bg-blueprint text-white border-2 border-blueprint font-bold uppercase tracking-wider text-xs hover:bg-blueprint/90"
              >
                Ver planes
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-3 py-2.5 text-sm ${
                      m.role === "user"
                        ? "bg-blueprint text-white"
                        : "bg-slate-100 text-ink border border-slate-200"
                    }`}>
                      {m.role === "assistant"
                        ? <MdText text={m.content} />
                        : <p>{m.content}</p>}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 border border-slate-200 px-3 py-2.5">
                      <div className="flex gap-1 items-center">
                        <span className="w-1.5 h-1.5 bg-blueprint rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-blueprint rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 bg-blueprint rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Starters — only on welcome state */}
              {messages.length === 1 && (
                <div className="px-3 pb-2 flex flex-wrap gap-1.5 shrink-0">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      disabled={loading}
                      className="px-2.5 py-1 text-[11px] border border-slate-200 text-steel hover:border-blueprint hover:text-blueprint transition-colors disabled:opacity-40"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="flex gap-2 p-3 border-t-2 border-slate-200 shrink-0">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Pregunta algo… (Enter para enviar)"
                  disabled={loading}
                  rows={1}
                  className="flex-1 bg-white border-2 border-slate-200 px-3 py-2 text-sm focus:border-blueprint focus:outline-none resize-none disabled:opacity-50 min-h-[40px]"
                  style={{ maxHeight: "80px", overflowY: "auto" }}
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="shrink-0 w-10 h-10 self-end flex items-center justify-center bg-blueprint text-white border-2 border-blueprint disabled:opacity-40 hover:bg-blueprint/90 transition-colors"
                >
                  <ArrowUp size={16} weight="bold" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
